import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, Spinner } from "react-bootstrap";
import { ArrowClockwise } from "react-bootstrap-icons";
import Icon from "@mdi/react";
import { mdiCalendarOutline } from "@mdi/js";
import {
  NylasDatePicker,
  NylasTimeslotPicker,
  NylasLocaleSwitch,
} from "@nylas/react";
import { fetchPreviewConfig } from "./slice";
import { formatDate } from "../PublicClientBooking/utils";
import "./clientBookingSettings.css";

const ClientViewPreview = ({ profile, userProfile, isEditMode, editFormData }) => {
  const dispatch = useDispatch();
  const { previewData, previewStatus } = useSelector(
    (state) => state.clientBookingSettings
  );

  // Local state for calendar interaction (preview-only, no booking action)
  const [selectedDate, setSelectedDate] = useState(null);
  // Timeslot selection state — not rendered, but needed to
  // let the NylasTimeslotPicker track user interaction.
  const [, setSelectedTimeslot] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(
    () => userProfile?.settings?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const durationOptions = useMemo(() => {
    const source = isEditMode && editFormData
      ? editFormData.durationRules
      : profile?.duration_rules;
    const rules = source || [];
    const sorted = [...rules].sort(
      (a, b) => (a.min_sqft || 0) - (b.min_sqft || 0)
    );
    const unique = [...new Set(sorted.map((r) => r.duration_minutes))].sort(
      (a, b) => a - b
    );
    return unique;
  }, [profile?.duration_rules, isEditMode, editFormData]);

  const [selectedDuration, setSelectedDuration] = useState(() => {
    return durationOptions[0] || 60;
  });

  // If selected duration is no longer in options, pick first
  useEffect(() => {
    if (durationOptions.length > 0 && !durationOptions.includes(selectedDuration)) {
      setSelectedDuration(durationOptions[0]);
    }
  }, [durationOptions, selectedDuration]);

  // Clear selection when preview data changes (new availability loaded)
  useEffect(() => {
    setSelectedDate(null);
    setSelectedTimeslot(null);
  }, [previewData]);

  const getPreviewParams = useCallback(() => {
    if (isEditMode && editFormData) {
      return {
        durationMinutes: selectedDuration,
        minBufferTimeMinutes: editFormData.bufferTime,
        maxDaysInFuture: editFormData.bookingWindow,
        weeklyHours: editFormData.weeklyHours,
      };
    }
    if (!profile) return null;
    return {
      durationMinutes: selectedDuration,
      minBufferTimeMinutes: profile.min_buffer_time_minutes,
      maxDaysInFuture: profile.max_booking_horizon_days,
      weeklyHours: profile.weekly_hours,
    };
  }, [isEditMode, editFormData, profile, selectedDuration]);

  const handleRefresh = useCallback(() => {
    const params = getPreviewParams();
    if (!params) return;
    dispatch(fetchPreviewConfig(params));
  }, [dispatch, getPreviewParams]);

  // Auto-fetch preview on mount when profile is available
  const hasFetched = useRef(false);
  useEffect(() => {
    if (profile && !hasFetched.current) {
      hasFetched.current = true;
      handleRefresh();
    }
  }, [profile, handleRefresh]);

  // Re-fetch when duration changes (read-only mode only)
  const prevDuration = useRef(selectedDuration);
  useEffect(() => {
    if (
      profile &&
      hasFetched.current &&
      prevDuration.current !== selectedDuration &&
      !isEditMode
    ) {
      prevDuration.current = selectedDuration;
      handleRefresh();
    }
  }, [selectedDuration, profile, handleRefresh, isEditMode]);

  // Derived availability data from preview response
  const availability = previewData?.availability || null;

  const selectableDates = useMemo(() => {
    if (!availability) return [];
    // Derive selectable dates from actual timeslot timestamps in the
    // selected timezone, so that e.g. LA Friday afternoon slots that
    // fall on Saturday in Taipei make Saturday selectable.
    const dateSet = new Set();
    Object.values(availability)
      .flat()
      .forEach((slot) => {
        const dateStr = new Date(slot.start_time).toLocaleDateString(
          "en-CA",
          { timeZone: selectedTimezone }
        );
        dateSet.add(dateStr);
      });
    return [...dateSet].map((dateStr) => new Date(dateStr + "T12:00:00Z"));
  }, [availability, selectedTimezone]);

  const flattenedAvailability = useMemo(() => {
    if (!availability) return [];
    return Object.values(availability)
      .flat()
      .map((slot) => ({
        emails: [],
        start_time: new Date(slot.start_time),
        end_time: new Date(slot.end_time),
      }));
  }, [availability]);

  const handleDateSelected = (event) => {
    const date = event.detail;
    if (!date) {
      setSelectedDate(null);
      setSelectedTimeslot(null);
      return;
    }
    // Extract YYYY-MM-DD in the selected timezone, then anchor to UTC noon
    // so the date stays stable across any browser timezone.
    const dateStr = (date instanceof Date ? date : new Date(date))
      .toLocaleDateString("en-CA", { timeZone: selectedTimezone });
    setSelectedDate(dateStr + "T12:00:00Z");
    setSelectedTimeslot(null);
  };

  const handleTimeslotSelected = (event) => {
    const timeslot = event.detail;
    if (!timeslot) {
      setSelectedTimeslot(null);
      return;
    }
    setSelectedTimeslot({
      start_time:
        timeslot.start_time instanceof Date
          ? timeslot.start_time.toISOString()
          : timeslot.start_time,
      end_time:
        timeslot.end_time instanceof Date
          ? timeslot.end_time.toISOString()
          : timeslot.end_time,
    });
  };

  const handleTimezoneChanged = (event) => {
    setSelectedTimezone(event.detail);
  };

  const renderScheduler = () => {
    if (previewStatus === "loading") {
      return (
        <div className="client-view-scheduler-placeholder">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading preview...
        </div>
      );
    }

    if (!availability || Object.keys(availability).length === 0) {
      return (
        <div className="client-view-scheduler-placeholder">
          {previewData
            ? "No available time slots"
            : "Preview will appear here"}
        </div>
      );
    }

    return (
      <div className="client-view-nylas-container">
        <div className="client-view-nylas-datepicker">
          <NylasDatePicker
            selectableDates={selectableDates}
            selectedDate={
              selectedDate ? new Date(selectedDate) : undefined
            }
            selectedTimezone={selectedTimezone}
            eventDuration={
              previewData?.duration_minutes || selectedDuration
            }
            onDateSelected={handleDateSelected}
          />
        </div>

        <div className="client-view-nylas-right">
          {selectedDate && (
            <div className="client-view-nylas-selected-date">
              <Icon path={mdiCalendarOutline} size={0.75} />
              <span>{formatDate(selectedDate, selectedTimezone)}</span>
            </div>
          )}
          <div className="client-view-nylas-timeslots">
            <NylasTimeslotPicker
              availability={flattenedAvailability}
              selectedDate={
                selectedDate ? new Date(selectedDate) : new Date()
              }
              selectedTimezone={selectedTimezone}
              onTimeslotSelected={handleTimeslotSelected}
            />
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="client-view-preview">
      <div className="client-view-header">
        <span className="client-view-title">Client View</span>
      </div>
      <div className="client-view-controls">
        <span className="client-view-duration-label">Duration</span>
        <Form.Select
          size="sm"
          className="client-view-duration-select"
          value={selectedDuration}
          onChange={(e) => setSelectedDuration(Number(e.target.value))}
        >
          {durationOptions.map((d) => (
            <option key={d} value={d}>
              {d} min
            </option>
          ))}
        </Form.Select>
        {isEditMode && (
          <Button
            variant="outline-primary"
            size="sm"
            className="cbs-preview-refresh-btn"
            onClick={handleRefresh}
            disabled={previewStatus === "loading"}
          >
            <ArrowClockwise size={14} className="me-1" />
            Refresh
          </Button>
        )}
      </div>

      <div className="client-view-scheduler">
        {renderScheduler()}
      </div>

      {availability && Object.keys(availability).length > 0 && (
        <div className="client-view-nylas-locale">
          <NylasLocaleSwitch
            selectedTimezone={selectedTimezone}
            onTimezoneChanged={handleTimezoneChanged}
          />
        </div>
      )}
    </div>
  );
};

export default ClientViewPreview;
